// @ts-strict-ignore
import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from "@angular/core";
import {animate, query, stagger, style, transition, trigger} from "@angular/animations";
import {Translations} from "@translations";
import {
	SimInternalStreamingService,
	SimMaterialDialogService,
	NotificationService,
	SimRecordingsService,
	SimTrainingRoomService,
	SimUtilityService,
	TrackByMethods,
} from "@services";
import {ExportState, ExportVisualState, FileSetConversionView, GetCurrentConversionsInternalResponse} from "@models";
import {TranslateService} from "@ngx-translate/core";

export interface WithRoomName {
	roomName: string;
}

@Component({
	selector: "sim-scenario-archive-filesets-panel",
	templateUrl: "./scenario-archive-filesets-panel.component.html",
	styleUrls: ["./scenario-archive-filesets-panel.component.scss"],
	animations: [
		trigger("listStagger", [
			transition("* <=> *", [
				query(
					":enter",
					[
						style({opacity: 0, transform: "translateY(-15px)"}),
						stagger(
							"50ms",
							animate(
								"550ms ease-out",
								style({
									opacity: 1,
									transform: "translateY(0px)",
								}),
							),
						),
					],
					{optional: true},
				),
				query(":leave", animate("0ms", style({opacity: 0})), {
					optional: true,
				}),
			]),
		]),
	],
	standalone: false,
})
export class ScenarioArchiveFilesetsPanelComponent implements OnInit, OnChanges, OnDestroy {
	T = Translations;
	TrackByMethods = TrackByMethods;

	@Input() scenarioRecordingId: string;

	private setsForScenarioRecordingWrapper: GetCurrentConversionsInternalResponse;

	public fileSets: FileSetConversionView[];
	public loadingSets: boolean = true;

	private checkTimer: any = null;
	ExportState = ExportState;
	ExportVisualState = ExportVisualState;

	constructor(
		public translate: TranslateService,
		private _simInternalStreamingService: SimInternalStreamingService,
		private _simDialogService: SimMaterialDialogService,
		private _simTrainingRoomService: SimTrainingRoomService,
		private _simNotificationService: NotificationService,
		private _simRecordingsService: SimRecordingsService,
	) {}

	ngOnChanges(changes: SimpleChanges) {
		if (this.scenarioRecordingId && !this.setsForScenarioRecordingWrapper) {
			this.loadSets();
		}
	}

	ngOnInit(): void {
		this.setConversionStatusCheckEnabled(true);
		/*
		if (this.reloadExportsEmitter) {
			this.reloadExportsEmitter.subscribe(() => {
				this.loadExports();
			});
		}*/
	}

	ngOnDestroy() {
		this.setConversionStatusCheckEnabled(false);
	}

	private setConversionStatusCheckEnabled(enabled: boolean) {
		if (this.checkTimer) {
			clearInterval(this.checkTimer);
		}

		if (enabled) {
			this.checkTimer = setInterval(async () => {
				this.loadSets(false);
			}, 3000);
		}
	}

	public loadSets(enableAutoOpenCloseSettingsCon = true) {
		if (this.scenarioRecordingId) {
			const loadingConversionsReq = this._simInternalStreamingService
				.getCurrentConversions(this.scenarioRecordingId)
				.then((setsForScenarioRecordingRes) => {
					this.setsForScenarioRecordingWrapper = setsForScenarioRecordingRes;
					this.fileSets = setsForScenarioRecordingRes.all;
				})
				.catch(() => {
					this.loadingSets = false;
				});

			Promise.all([loadingConversionsReq, SimUtilityService.delay(1000)]).then(() => {
				this.loadingSets = false;
			});
		}
	}

	public dismissConversion(conversionId: string) {
		this._simInternalStreamingService.dismiss({conversionProcessInfoId: conversionId}).then((dismissExportResult) => {
			this.loadSets();
		});
	}

	public pauseConversion(conversionId: string) {
		this._simInternalStreamingService.pause({conversionProcessInfoId: conversionId}).then((pauseExportResult) => {
			this.loadSets();
		});
	}

	public deleteConversion(conversionId: string) {
		this._simDialogService
			.openDeleteDialog({
				deleteTitle: this.translate.instant(this.T.RECORDING_FILESET_CONVERSION_DIALOG_DELETE_TITLE),
				deleteText: this.translate.instant(this.T.RECORDING_FILESET_CONVERSION_DIALOG_DELETE_DESCRIPTION),
				deleteButtonText: this.translate.instant(this.T.GLOBAL_TEXT_YES),
				cancelText: this.translate.instant(this.T.GLOBAL_TEXT_NO),
			})
			.then((data) => {
				if (data.action == "ok") {
					this._simInternalStreamingService.deleteConversion({conversionJobId: conversionId}).then((pauseExportResult) => {
						this.loadSets();
					});
				}
			});
	}

	public cancelConversion(conversionId: string) {
		this._simDialogService
			.openDeleteDialog({
				deleteTitle: this.translate.instant(this.T.RECORDING_FILESET_CONVERSION_DIALOG_CANCEL_TITLE),
				deleteText: this.translate.instant(this.T.RECORDING_FILESET_CONVERSION_DIALOG_CANCEL_DESCRIPTION),
				deleteButtonText: this.translate.instant(this.T.GLOBAL_TEXT_YES),
				cancelText: this.translate.instant(this.T.GLOBAL_TEXT_NO),
			})
			.then((data) => {
				if (data.action == "ok") {
					this._simInternalStreamingService.cancel({conversionProcessInfoId: conversionId}).then((pauseExportResult) => {
						this.loadSets();
					});
				}
			});
	}

	public resumeConversion(conversionId: string) {
		this._simInternalStreamingService.resume({conversionProcessInfoId: conversionId}).then((pauseExportResult) => {
			this.loadSets();
		});
	}
}
